/* ==========================================================================
   Olympus quick-buy sheet (CRO pass 2026-09-30) — no external libraries.
   Markup: snippets/az-quickbuy.liquid (rendered once from sections/az-set-bonus.liquid).
   Styles: assets/az-quickbuy.css.

   - The sticky mobile bar's "Add to cart" link ([data-az-qb-open], href="#tee") is
     swapped for a real <button> that opens a native modal <dialog> bottom sheet
     (focus trap, Escape, top layer). No JS -> the link still jumps to #tee.
   - Garment toggle (tee / long sleeve / hoodie), size buttons (none preselected),
     price = selected variant price; while html.az-promo-on the launch price is shown
     with the same maths as the buy sections: price - floor(price * 15 / 100).
   - Add: ONE item to /cart/add.js with `sections`, announced through Horizon's
     CartLinesUpdateEvent (same pattern as initSet in olympus-landing.js), then the
     sheet closes and the cart drawer opens. Fallbacks: open theme-drawer#cart-drawer,
     click the cart icon, redirect to /cart.
   ========================================================================== */

const QB_OFF_PCT = 15;

const onReady = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());

let eventsLib = null;
async function loadEvents() {
  if (eventsLib !== null) return eventsLib;
  try {
    eventsLib = await import('@shopify/events');
  } catch (e) {
    eventsLib = false;
  }
  return eventsLib;
}

function money(cents, currency) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
  } catch (e) {
    return '$' + (cents / 100).toFixed(2);
  }
}

function cartRoutes() {
  const r = (window.Theme && window.Theme.routes) || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return {
    add: r.cart_add_url ? r.cart_add_url.replace(/(\.js)?$/, '.js') : root + 'cart/add.js',
    cart: r.cart_url || root + 'cart',
  };
}

function openCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return true;
  }
  const icon = document.querySelector('.header-actions__action.action__cart, a[href$="/cart"].header-actions__action, #cart-icon-bubble');
  if (icon) {
    icon.click();
    return true;
  }
  return false;
}

const promoOn = () => document.documentElement.classList.contains('az-promo-on');
const launchPrice = (cents) => cents - Math.floor((cents * QB_OFF_PCT) / 100);
const mobile = window.matchMedia('(max-width: 749px)');

// The one live sheet. Module-level so the <html> observer and the media-query listener
// at the bottom are registered once, even when the theme editor re-renders the section.
let currentSheet = null;

/* ------------------------------------------------------------------ sheet */
function initSheet(dialog) {
  if (dialog.__azQb) return dialog.__azQb;
  if (typeof dialog.showModal !== 'function') return null;

  let data;
  try {
    data = JSON.parse(dialog.querySelector('[data-az-qb-data]').textContent);
  } catch (e) {
    return null;
  }

  // Theme editor re-renders: drop any older sheet we moved to <body> before.
  document.querySelectorAll('dialog[data-az-quickbuy]').forEach((d) => {
    if (d !== dialog && d.parentElement === document.body) d.remove();
  });
  // Out of the section (it fades in with a transform) and next to the body-level bar.
  if (dialog.parentElement !== document.body) document.body.appendChild(dialog);

  const currency = dialog.dataset.currency || 'USD';
  const $ = (sel) => dialog.querySelector(sel);
  const garmentBtns = Array.from(dialog.querySelectorAll('[data-az-qb-garment]'));
  const img = $('[data-az-qb-img]');
  const title = $('[data-az-qb-title]');
  const price = $('[data-az-qb-price]');
  const sizesBox = $('[data-az-qb-sizes]');
  const msg = $('[data-az-qb-msg]');
  const add = $('[data-az-qb-add]');
  const addLabel = $('[data-az-qb-add-label]');
  const chart = $('[data-az-qb-chart]');

  // size = the size applied to the current garment; wanted = the last size the shopper
  // tapped, kept across garments and re-applied whenever the current garment has it.
  const state = { garment: 'tee', size: null, wanted: null, busy: false, opener: null };
  if (!data[state.garment]) state.garment = garmentBtns.map((b) => b.dataset.azQbGarment).find((k) => data[k]) || 'tee';

  const product = () => data[state.garment];
  const variant = () => {
    const p = product();
    return p && state.size ? p.variants.find((v) => v.size === state.size) || null : null;
  };

  // The message line always keeps its one-line height (see CSS), so showing/hiding a
  // message never moves anything above it in the bottom-anchored sheet.
  const say = (text, isError) => {
    const t = text || '';
    if (msg.textContent !== t) msg.textContent = t;
    msg.classList.toggle('az-qb__msg--error', Boolean(t && isError));
  };

  // Built once; later renders only touch text that actually changed, so the polite live
  // region doesn't re-announce the same price.
  price.textContent = '';
  const priceNow = document.createElement('span');
  priceNow.className = 'az-qb__now';
  const priceWas = document.createElement('s');
  priceWas.className = 'az-qb__was';
  const priceLaunch = document.createElement('span');
  priceLaunch.className = 'az-qb__launch';
  priceLaunch.textContent = 'Sale price · 15% off, applied in your cart';
  price.append(priceNow, ' ', priceWas, priceLaunch);
  const setText = (el, t) => {
    if (el.textContent !== t) el.textContent = t;
  };
  const setHidden = (el, h) => {
    if (el.hidden !== h) el.hidden = h;
  };

  const renderPrice = () => {
    const p = product();
    if (!p) return;
    const v = variant();
    const cents = v ? v.price : Math.min(...p.variants.map((x) => x.price));
    const varies = !v && new Set(p.variants.map((x) => x.price)).size > 1;
    const from = varies ? 'From ' : '';
    const promo = promoOn();
    setText(priceNow, from + money(promo ? launchPrice(cents) : cents, currency));
    setText(priceWas, promo ? money(cents, currency) : '');
    setHidden(priceWas, !promo);
    setHidden(priceLaunch, !promo);
  };

  // "S–2XL" range for the neutral note when the wanted size doesn't exist.
  const rangeOf = (p) => {
    const sizes = p.variants.map((x) => x.size);
    return sizes.length > 1 ? sizes[0] + '–' + sizes[sizes.length - 1] : sizes[0] || '';
  };
  const garmentLabel = (key) => {
    const b = garmentBtns.find((x) => x.dataset.azQbGarment === key);
    return b ? b.textContent.trim() : key;
  };

  // Apply state.wanted to the current garment. Returns a neutral note when it can't.
  const applyWanted = () => {
    const p = product();
    state.size = null;
    if (!p || !state.wanted) return '';
    const v = p.variants.find((x) => x.size === state.wanted);
    if (v && v.available) {
      state.size = v.size;
      return '';
    }
    if (v) return state.wanted + ' is sold out in ' + garmentLabel(state.garment).toLowerCase() + '.';
    return garmentLabel(state.garment) + ' comes in ' + rangeOf(p) + '.';
  };

  const renderAdd = () => {
    const ready = Boolean(variant()) && !state.busy;
    add.setAttribute('aria-disabled', ready ? 'false' : 'true');
    addLabel.textContent = state.busy ? 'Adding…' : 'Add to cart';
  };

  const renderSizes = () => {
    const p = product();
    sizesBox.textContent = '';
    if (!p) return;
    p.variants.forEach((v) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'az-qb__size';
      b.dataset.size = v.size;
      b.setAttribute('aria-pressed', String(v.size === state.size));
      b.textContent = v.size;
      if (!v.available) {
        b.disabled = true;
        b.classList.add('az-qb__size--soldout');
        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = ' (sold out)';
        b.appendChild(sr);
      }
      sizesBox.appendChild(b);
    });
  };

  const render = () => {
    const p = product();
    if (!p) return;
    garmentBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.azQbGarment === state.garment)));
    title.textContent = p.title;
    if (p.img) {
      img.src = p.img;
      img.srcset = p.img + ' 1x, ' + (p.img2x || p.img) + ' 2x';
      img.alt = p.alt || '';
      img.hidden = false;
    } else {
      img.hidden = true;
    }
    // Size chart: olympus-landing.js opens any [data-az-sizechart] with showModal(), so the
    // chart stacks above this sheet in the top layer; we only point it at the garment.
    chart.setAttribute('data-az-sizechart', state.garment);
    renderSizes();
    renderPrice();
    renderAdd();
  };

  garmentBtns.forEach((b) =>
    b.addEventListener('click', () => {
      const key = b.dataset.azQbGarment;
      if (!data[key] || key === state.garment || state.busy) return;
      state.garment = key;
      // Re-apply the last size the shopper tapped (tee 3XL -> long sleeve -> hoodie keeps 3XL).
      const note = applyWanted();
      sizesBox.classList.remove('az-qb__sizes--invalid');
      say(note, false);
      render();
    })
  );

  sizesBox.addEventListener('click', (event) => {
    const b = event.target instanceof Element ? event.target.closest('.az-qb__size') : null;
    if (!b || b.disabled || state.busy) return;
    state.size = b.dataset.size;
    state.wanted = b.dataset.size;
    sizesBox.querySelectorAll('.az-qb__size').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    sizesBox.classList.remove('az-qb__sizes--invalid');
    say('');
    renderPrice();
    renderAdd();
  });

  add.addEventListener('click', async () => {
    if (state.busy) return;
    const v = variant();
    if (!v) {
      say('Pick a size.', true);
      sizesBox.classList.add('az-qb__sizes--invalid');
      const first = sizesBox.querySelector('.az-qb__size:not(:disabled)');
      (first || sizesBox).focus();
      return;
    }
    state.busy = true;
    add.setAttribute('aria-busy', 'true');
    dialog.classList.add('az-qb--busy');
    say('');
    renderAdd();

    const items = [{ id: Number(v.id), quantity: 1 }];
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset && el.dataset.sectionId)
      .filter(Boolean);

    const lib = await loadEvents();
    let deferred = null;
    if (lib && lib.CartLinesUpdateEvent) {
      try {
        deferred = lib.CartLinesUpdateEvent.createPromise();
        add.dispatchEvent(
          new lib.CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
            promise: deferred.promise,
          })
        );
      } catch (e) {
        deferred = null;
      }
    }

    const routes = cartRoutes();
    let succeeded = false;
    try {
      const res = await fetch(routes.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(sectionIds.length ? { items, sections: sectionIds.join(',') } : { items }),
      });
      const resData = await res.json().catch(() => ({}));
      const failed = !res.ok || resData.status;

      let cart = null;
      try {
        cart = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      } catch (e) {
        /* ignore */
      }

      if (!failed) {
        succeeded = true;
        // Close first so focus goes back to the bar before the drawer takes it.
        close();
      }

      if (deferred) {
        if (cart && lib.CartLinesUpdateEvent.createCartFromAjaxResponse) {
          deferred.resolve({
            cart: lib.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'olympus-quick-buy',
              itemCount: failed ? 0 : 1,
              sections: failed ? undefined : resData.sections,
              didError: Boolean(failed),
            },
          });
        } else {
          deferred.reject(Object.assign(new Error('cart refresh failed'), { name: 'AbortError' }));
        }
      }

      if (failed) {
        const text = (resData && (resData.description || resData.message)) || '';
        // Short one-line messages: the message line has a fixed height.
        say(/sold out|already sold|not available|inventory/i.test(text) ? 'That size just sold out.' : 'Couldn’t add. Please try again.', true);
        if (lib && lib.CartErrorEvent) {
          try {
            add.dispatchEvent(new lib.CartErrorEvent({ error: resData.message || 'Add to cart failed', code: 'INVALID' }));
          } catch (e) {
            /* ignore */
          }
        }
        return;
      }

      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!openCartDrawer()) window.location.href = routes.cart;
        }, 60);
      });
    } catch (error) {
      if (deferred) deferred.reject(Object.assign(new Error('network'), { name: 'AbortError' }));
      say('Network hiccup. Please try again.', true);
    } finally {
      state.busy = false;
      add.removeAttribute('aria-busy');
      dialog.classList.remove('az-qb--busy');
      if (succeeded) {
        // Fresh start next time: same garment, no size.
        state.size = null;
        state.wanted = null;
        say('');
        render();
      } else {
        renderAdd();
      }
    }
  });

  /* open / close */
  const lock = (on) => document.documentElement.classList.toggle('az-qb-open', on);

  function open(opener) {
    if (dialog.open || !mobile.matches) return;
    state.opener = opener || null;
    say('');
    sizesBox.classList.remove('az-qb__sizes--invalid');
    render();
    dialog.showModal();
    lock(true);
    // Focus the first garment button rather than the close button (showModal's default).
    const first = garmentBtns.find((b) => b.getAttribute('aria-pressed') === 'true') || garmentBtns[0];
    if (first) first.focus({ preventScroll: true });
  }

  // Idempotent: runs from close() AND from the dialog's own 'close' event (Escape etc.),
  // so the page can never stay scroll-locked; focus goes back to the opener once.
  const afterClose = () => {
    lock(false);
    const o = state.opener;
    state.opener = null;
    if (o && o.isConnected && typeof o.focus === 'function') {
      try {
        o.focus({ preventScroll: true });
      } catch (e) {
        /* ignore */
      }
    }
  };

  function close() {
    if (dialog.open) dialog.close();
    afterClose();
  }

  dialog.addEventListener('close', afterClose);
  // Belt and braces: the 'close' event is queued (and can lag when the page isn't painting);
  // the [open] attribute is removed synchronously on every close path (Escape, other code).
  new MutationObserver(() => {
    if (!dialog.open) afterClose();
  }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  dialog.addEventListener('cancel', (event) => {
    if (state.busy) event.preventDefault();
  });

  // Backdrop tap: the panel fills the dialog box, so a press AND release on the dialog
  // element itself is the backdrop (a drag that starts inside the panel doesn't count).
  let downOnBackdrop = false;
  dialog.addEventListener('pointerdown', (event) => {
    downOnBackdrop = event.target === dialog;
  });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog && downOnBackdrop && !state.busy) close();
    downOnBackdrop = false;
    if (event.target instanceof Element && event.target.closest('[data-az-qb-close]') && !state.busy) close();
  });

  render();
  const api = {
    open,
    close,
    // Called by the module-level observers at the bottom of this file.
    onPromoChange: () => {
      if (dialog.open) renderPrice();
    },
    onDesktop: close,
  };
  dialog.__azQb = api;
  currentSheet = api;
  return api;
}

/* ------------------------------------------------------------- bar button */
function initOpeners() {
  document.querySelectorAll('[data-az-qb-open]').forEach((el) => {
    if (el.tagName === 'BUTTON' || el.dataset.azQbReady) return;
    // Swap the no-JS link (href="#tee") for a real button so the anchor-scroll and
    // sleeve-toggle handlers in olympus-landing.js no longer match it.
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = el.className;
    btn.innerHTML = el.innerHTML;
    btn.setAttribute('data-az-qb-open', '');
    btn.setAttribute('aria-haspopup', 'dialog');
    btn.setAttribute('aria-controls', 'az-quickbuy');
    btn.dataset.azQbReady = '1';
    el.replaceWith(btn);
  });
}

function boot() {
  const dialog = document.querySelector('dialog[data-az-quickbuy]');
  if (!dialog) return;
  const sheet = initSheet(dialog);
  if (!sheet) return; // leave the #tee link in place
  initOpeners();
}

if (!window.__azQuickBuy) {
  window.__azQuickBuy = true;
  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target.closest('button[data-az-qb-open]') : null;
    if (!el) return;
    const dialog = document.getElementById('az-quickbuy');
    const sheet = dialog && dialog.__azQb;
    if (!sheet) return;
    event.preventDefault();
    sheet.open(el);
  });
  onReady(boot);

  // Singletons (not per sheet): promo can end while the sheet is open, so re-render the
  // price only when html.az-promo-on actually flips.
  let lastPromo = promoOn();
  new MutationObserver(() => {
    const now = promoOn();
    if (now === lastPromo) return;
    lastPromo = now;
    if (currentSheet) currentSheet.onPromoChange();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

  // Crossed to desktop while open: the sheet is phone-only.
  const onMq = () => {
    if (!mobile.matches && currentSheet) currentSheet.onDesktop();
  };
  if (mobile.addEventListener) mobile.addEventListener('change', onMq);
  else if (mobile.addListener) mobile.addListener(onMq);
  // Theme editor: the set-bonus section (bar + sheet) re-renders in place.
  document.addEventListener('shopify:section:load', () => {
    const fresh = Array.from(document.querySelectorAll('dialog[data-az-quickbuy]')).find((d) => !d.__azQb);
    if (!fresh) return;
    document.querySelectorAll('dialog[data-az-quickbuy]').forEach((d) => {
      if (d !== fresh) {
        if (d.__azQb) d.__azQb.close();
        else if (d.open) d.close();
        d.remove();
      }
    });
    const sheet = initSheet(fresh);
    if (sheet) initOpeners();
  });
}
